import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Body,
  Button,
  Card,
  Heading,
  I,
  InfoCallout,
  Stack,
  TextLink,
} from '@leanin/ui';
import { FlowShell, type FlowStep } from '@/components/onboarding/FlowShell';
import { Picture } from '@/components/media/Picture';
import circlesDirectory from '@/assets/onboarding/circles-directory.png?w=440;880;1320&format=avif;webp;png&as=picture';

// Lean In Member onboarding tour — step 2 of 3.
// Frames Circles as the headline way to get involved — three short
// paragraphs of context, a directory screenshot, and a closing
// nudge toward Circle Leader Training (external leanin.org/events
// link) for members who can't find a Circle to join.

const STEPS: ReadonlyArray<FlowStep> = [
  { label: 'Welcome' },
  { label: 'Lean In Circles' },
  { label: 'Get involved' },
  { label: 'Build leadership skills' },
  { label: "You're in" },
];

export const Route = createFileRoute('/onboarding/circles')({
  component: OnboardingCirclesPage,
});

function OnboardingCirclesPage() {
  const navigate = useNavigate();
  return (
    <FlowShell
      role="Lean In Member"
      stepIndex={1}
      steps={STEPS}
      minutes={1}
      title="Connect, learn, and support each other."
      primary={
        <Button
          variant="primary"
          iconRight={I.arrow}
          onClick={() => navigate({ to: '/onboarding/get-involved' })}
        >
          Next
        </Button>
      }
      secondary={
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '/onboarding/welcome' })}
        >
          Back
        </Button>
      }
    >
      <Stack gap="lg">
        <Stack gap="md">
          <Heading level={2}>Discover Lean In Circles</Heading>
          <Body size="md">
            One of the best ways to get involved is by joining a Lean In
            Circle: small groups that meet regularly to share experiences,
            exchange advice, build confidence, and support one another
            through real conversations.
          </Body>
          <Body size="md">
            Women in Circles are{' '}
            <strong style={{ color: 'var(--primary)' }}>
              2x more likely to get a promotion or receive a raise
            </strong>{' '}
            and{' '}
            <strong style={{ color: 'var(--primary)' }}>
              71% say they have the support they need to advance their
              careers
            </strong>
            .
          </Body>
          <Body size="md">
            Circles meet once a month — either in person or online. You
            don’t need to have everything figured out to participate. You
            just need to show up as yourself.
          </Body>
          <Body size="md">
            Once you’re in the community, browse our Circle directory to
            find a Circle to join.
          </Body>
        </Stack>

        <Card pad="flush" clip>
          <Picture
            image={circlesDirectory}
            alt="Circles directory page with search, filters, and a grid of Circle cards"
            sizes="(max-width: 720px) 100vw, 880px"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </Card>

        <InfoCallout tone="lilac" pad="default">
          Can’t find a Circle for you – start your own! Sign up for an
          upcoming{' '}
          <TextLink
            href="https://leanin.org/events"
            target="_blank"
            rel="noopener noreferrer"
          >
            Circle Leader Training
          </TextLink>{' '}
          to learn more.
        </InfoCallout>
      </Stack>
    </FlowShell>
  );
}
